import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@mantine/core';
import BulletinSection from '@/components/common/BulletinSection';
import AttestationStep from '@/components/app/deals/steps/AttestationStep';
import ShipmentScheduleStep from '@/components/app/deals/steps/ShipmentScheduleStep';
import SpecSigningStep from '@/components/app/deals/steps/SpecSigningStep';
import InvoicesStep from '@/components/app/deals/steps/InvoicesStep';
import ShipmentConfirmationStep from '@/components/app/deals/steps/ShipmentConfirmationStep';
import ConsigneeStep from '@/components/app/deals/steps/ConsigneeStep';
import ForwarderAssignmentSigningStep from '@/components/app/deals/steps/ForwarderAssignmentSigningStep';
import CarrierAssignStep from '@/components/app/deals/steps/CarrierAssignStep';
import ForwardingStep from '@/components/app/deals/steps/ForwardingStep';
import FinalPaymentStep from '@/components/app/deals/steps/FinalPaymentStep';
import UpdTorgStep from '@/components/app/deals/steps/UpdTorgStep';
import ClosingStep from '@/components/app/deals/steps/ClosingStep';
import AttestationGateBanner from '@/components/app/deals/AttestationGateBanner';
import { forwardingStageTitleKey } from '@/lib/forwarding';
import { useAttestationRequired } from '@/hooks/useAttestationRequired';
import { useIsAgent } from '@/hooks/useRoles';
import type { BargainStepsInfo } from '@/types/deals';

interface DealStepPanelProps {
  readonly dealId: number;
  readonly info: BargainStepsInfo;
}

// Панель действия текущего шага: рендерит UI по processingStep. Возвращает null для шагов без
// реализованных в этой фазе действий (степпер и шапка уже отражают состояние — мёртвого UI нет).
// Фаза 2: WAIT_ATTESTATION. Следующие шаги добавляются ветками здесь же.
export default function DealStepPanel({ dealId, info }: DealStepPanelProps) {
  const { t } = useTranslation();
  const attestationRequired = useAttestationRequired();
  // Агент не аттестуется — гейт аттестации к нему не применяем (страховка на случай isVerified!==true).
  const isAgent = useIsAgent();
  let body: ReactNode = null;

  switch (info.processingStep) {
    case 'WAIT_ATTESTATION':
      body = <AttestationStep dealId={dealId} info={info} />;
      break;
    case 'SHIPMENT_SCHEDULE_PROCESSING':
    case 'WAIT_SELLER_SEND_SCHEDULE':
    case 'WAIT_BUYER_CONFIRM_SCHEDULE':
    case 'WAIT_SELLER_CONFIRM_SCHEDULE':
    case 'SHIPMENT_SCHEDULE_CONFIRMED':
      body = <ShipmentScheduleStep dealId={dealId} info={info} />;
      break;
    case 'WAIT_BUYER_SPEC_SIGNED':
    case 'WAIT_SELLER_SPEC_SIGNED': // легаси-шаг: BPMN его не эмитит, но обрабатываем, чтобы не остаться без панели
    case 'WAIT_AGENT_SPEC_SIGNED':
    case 'SPECIFICATION_COMPLETED':
      // Подпись спецификации — двусторонняя (агент → покупатель). Продавец в ней не участвует: на
      // бэкенде эти шаги для него — стадия «Подтверждение отгрузки» (ожидание), её и показываем (без
      // очереди подписи и спецификации). Остальные (агент/покупатель — участники, банк — наблюдатель)
      // видят экран подписи; наблюдателю он даёт нейтральное «ожидаем подписи».
      body =
        info.isSeller === true ? (
          <ShipmentConfirmationStep dealId={dealId} info={info} />
        ) : (
          <SpecSigningStep dealId={dealId} info={info} />
        );
      break;
    case 'WAIT_INVOICES_GENERATION':
    case 'WAIT_LOGISTIC_INVOICE_GENERATION':
    case 'PAYMENT_PROCESSING':
      body = <InvoicesStep info={info} />;
      break;
    case 'WAIT_SHIPMENT_IS_READY_CONFIRMATION':
    case 'SHIPMENT_READY_CONFIRMED':
      body = <ShipmentConfirmationStep dealId={dealId} info={info} />;
      break;
    case 'WAIT_CONSIGNEE_ASSIGNED':
      body = <ConsigneeStep dealId={dealId} info={info} />;
      break;
    case 'WAIT_AGENT_FORWARDER_ASSIGNMENT_SIGNED':
      body = <ForwarderAssignmentSigningStep dealId={dealId} info={info} />;
      break;
    // Ожидание перевозчика и его назначение — отдельный шаг перед «Перевозкой», а не её начало.
    case 'WAIT_FORWARDING_STARTING':
    case 'CARRIER_ASSIGNED':
      body = <CarrierAssignStep info={info} />;
      break;
    case 'FORWARDING_PROCESSING':
    case 'WAIT_FORWARDER_ACCEPT_ORDER':
    case 'FORWARDER_ACCEPT_ORDER':
    case 'FORWARDING_IN_PROGRESS':
    case 'FORWARDER_REJECT_ORDER':
    case 'FORWARDING_COMPLETED':
    case 'WAIT_DELIVERY':
    case 'WAIT_WEIGHT_MODERATION':
    case 'DELIVERY_COMPLETED':
      body = <ForwardingStep dealId={dealId} info={info} />;
      break;
    case 'FINAL_PAYMENT_PROCESSING':
    case 'WAIT_UPLOAD_UNDERPAYMENT':
    case 'WAIT_UNDERPAYMENT':
    case 'WAIT_WIRE_UNDERPAYMENT_CONFIRMATION':
    case 'WAIT_UPLOAD_OVERPAYMENT':
    case 'WAIT_OVERPAYMENT':
    case 'WAIT_RETURN_OVERPAYMENT_CONFIRMATION':
    case 'WAIT_UPLOAD_UKD':
    case 'FINAL_PAYMENT_DONE':
      body = <FinalPaymentStep dealId={dealId} info={info} />;
      break;
    case 'WAIT_UPD_TORG12_GENERATION':
    case 'WAIT_UPD_TORG12_SENDING':
    case 'WAIT_UPLOAD_SIGNED_UPD_TORG12':
    case 'UPD_TORG12_SIGNED':
      body = <UpdTorgStep info={info} />;
      break;
    case 'BARGAIN_CLOSING':
    case 'BARGAIN_CLOSED':
    case 'BARGAIN_COMPLETED':
      body = <ClosingStep info={info} />;
      break;
    default:
      return null;
  }

  // Ограничение действий до аттестации: не аттестованному покупателю показываем предупреждение вместо
  // действий текущего шага. Панель есть только у реализованных шагов (нереализованные и терминальные без
  // действий уже вернули null выше), поэтому лишней карточки на пустых шагах не появляется; просмотр
  // (шапка, документы, степпер) остаётся доступен.
  if (info.isBuyer && attestationRequired && !isAgent) {
    body = <AttestationGateBanner />;
  }

  const titleKey = forwardingStageTitleKey(info.processingStep ?? '');
  const name = titleKey ? t(titleKey) : info.stage;
  // «Шаг 1 из 12. Аттестация» — номер по сквозной нумерации стороны (FR-BRG-40); без номера — только название.
  const steps = info.stepsList ?? [];
  const current = steps.find((step) => step.isCurrent);
  const title =
    current?.num != null && steps.length > 0
      ? t('app.deals.header.stepTitle', { num: current.num, total: steps.length, name: name ?? current.name })
      : name;

  return (
    <BulletinSection
      title={title}
      spaced
      // «Ваш ход» у названия шага заменил прежнюю плашку «Требуется ваше действие» в шапке.
      aside={
        info.actionRequired ? (
          <Text span fz="sm" fw={600} c="brand.7">
            {t('app.deals.stepper.yourTurn')}
          </Text>
        ) : null
      }
    >
      {body}
    </BulletinSection>
  );
}
